import React, {useState, useEffect, useRef, forwardRef, SyntheticEvent} from 'react'
import _ from 'lodash'
import {Dimmer, Loader, Progress} from 'semantic-ui-react'
import {secondsToTimeString, useConfig} from '../..'
import './style.css'

export interface IVideoPlayerProps
  extends Omit<React.VideoHTMLAttributes<HTMLVideoElement>, 'onTimeUpdate'> {
  videoUrl?: string
  dataUrl?: string
  fileName?: string
  // header?: React.ReactNode,
  videoRef?: React.RefObject<HTMLVideoElement>
  // mimeType,
  disabled?: boolean
  autoPlay?: boolean
  seekOffset?: number
  range?: [number, number]
  clampToRange?: boolean
  pauseAtRangeStart?: boolean
  pauseAtRangeEnd?: boolean
  loop?: boolean
  disableDownload?: boolean
  disableFullscreen?: boolean
  overlay?: React.ReactNode
  videoStyle?: React.CSSProperties
  actions?: React.ReactNode
  verifyCanPlay?: boolean
  playbackRate?: number
  onTimeUpdate?: (e: ITimeUpdateArgs) => void
  onRangeEnd?: () => void
}

export interface ITimeUpdateArgs {
  percent: number
  time: number
  duration: number
}

export const VideoPlayer = forwardRef<HTMLDivElement, IVideoPlayerProps>((props, ref) => {
  const {
    videoUrl,
    dataUrl,
    fileName,
    // header,
    style,
    videoRef,
    videoStyle,
    // mimeType,
    seekOffset,
    range,
    clampToRange,
    pauseAtRangeStart,
    pauseAtRangeEnd,
    loop,
    disabled,
    disableDownload,
    disableFullscreen,
    overlay,
    autoPlay,
    verifyCanPlay,
    actions,
    playbackRate,
    onTimeUpdate,
    onRangeEnd,
    ...rest
  } = {
    onTimeUpdate: undefined,
    // mimeType: 'video/mp4',
    disabled: false,
    disableDownload: true,
    disableFullscreen: true,
    autoPlay: false,
    verifyCanPlay: true,
    ...props,
  }

  const {formatTime} = useConfig()

  // const [dataFrame, setDataFrame] = useState();
  const [currentTime, setCurrentTime] = useState<number>(0)
  const [isBusy, setIsBusy] = useState(false)

  const _ref = useRef<HTMLVideoElement>(null)
  const textRef = useRef<HTMLTrackElement>(null)

  const shouldHandleProgress = range !== undefined && clampToRange

  const supportedVideo = ['mp4', 'webm', 'ogg']

  // useImperativeHandle(ref, () => {
  //     return videoRef.current;
  // })

  // const onCueChange = (e) => {
  //     var cue = _.get(e, 'target.activeCues[0]');
  //     console.log('onCueChange()', cue);
  //     if (cue !== undefined)
  //     {
  //         var data = JSON.parse(cue.text);
  //         setDataFrame(data);
  //     }
  //     else
  //     {
  //         setDataFrame();
  //     }
  // }

  // useEffect(() => {
  //     if (!textRef.current) return;
  //     textRef.current.track.oncuechange = onCueChange;
  //     return () => {
  //         if (!textRef.current) return;
  //         textRef.current.track.oncuechange = undefined;
  //     };
  // }, []);

  // useEffect(() => {
  //     const ref = getRef();
  //     function animate(now:number, metadata:VideoFrameCallbackMetadata)
  //     {
  //         console.log('>>>', now, metadata);
  //         ref.current?.requestVideoFrameCallback(animate);
  //     }
  //     ref.current?.requestVideoFrameCallback(animate)
  // }, []);

  useEffect(() => {
    if (!videoUrl) {
      const ref = getRef()
      if (!ref.current) return
      ref.current.srcObject = null
    }
  }, [videoUrl])

  useEffect(() => {
    const ref = getRef()
    if (!ref.current) return
    if (ref.current.playbackRate === playbackRate) return
    ref.current.playbackRate = playbackRate || 1
  }, [playbackRate])

  useEffect(() => {
    if (disabled) return
    if (!canPlayCurrentVideo) return
    const ref = getRef()
    if (!ref.current) return
    if (!(range && range.length)) return
    // if (_videoRef.current.currentTime >= range[0] && _videoRef.current.currentTime <= range[1]) return;
    ref.current.currentTime = range[0] + (seekOffset || 0)
    // if (autoPlay && ref.current.paused) ref.current.play();
  }, [range])

  const defaultVideoStyle: React.CSSProperties = {
    width: '100%',
    // gridColumn: 1,
  }

  function getRef() {
    return videoRef || _ref
  }

  function toggleIsPlaying() {
    if (disabled) return
    if (!canPlayCurrentVideo) return
    const ref = getRef()
    if (!ref.current) return
    if (ref.current.paused) ref.current.play()
    else ref.current.pause()
  }

  function handleUpdateTime(e: SyntheticEvent<HTMLVideoElement, Event>) {
    const target = e.target as HTMLVideoElement
    setCurrentTime(target.currentTime)
    if (onTimeUpdate !== undefined) {
      onTimeUpdate({
        percent: target.currentTime / target.duration,
        time: target.currentTime,
        duration: target.duration,
      })
    }
  }

  function handleOnProgressClick(e: React.MouseEvent<HTMLDivElement> | undefined) {
    const target = e?.target as HTMLDivElement
    const bounds = target.getBoundingClientRect()
    const x = e?.clientX
    if (x === undefined) return
    const percent = (x - bounds.x) / bounds.width
    if (!range) return
    const t = range[0] + (range[1] - range[0]) * percent
    const ref = getRef()
    if (!ref.current) return
    ref.current.currentTime = t
  }

  function extensionFromFilenameOrUrl(fileName?: string, videoUrl?: string): string | undefined {
    if (!fileName && !videoUrl) return
    const match =
      fileName ? fileName.match(/^.*\.(.*)$/)
      : videoUrl ? videoUrl.match(/^.*\/.*\.(.*)\?/)
      : undefined
    return match ? _.toLower(match[1]) : undefined
  }

  function canPlayVideo(fileName?: string, videoUrl?: string): [boolean, string | undefined] {
    if (verifyCanPlay !== true) return [true, undefined]
    const identExtension = extensionFromFilenameOrUrl(fileName, videoUrl)
    if (!identExtension) return [true, undefined]
    return [(identExtension && _.includes(supportedVideo, identExtension)) || false, identExtension]
  }

  const [canPlayCurrentVideo, videoExtension] = canPlayVideo(fileName, videoUrl)

  return (
    <div
      className='video-player'
      style={style}
      ref={ref}
    >
      {/* {header ? <Header>{header}</Header> : null} */}
      <video
        ref={getRef()}
        src={canPlayCurrentVideo ? videoUrl : ''}
        // type={mimeType}
        autoPlay={autoPlay}
        controls={shouldHandleProgress ? false : true}
        disablePictureInPicture={true}
        controlsList={_.chain([
          disableDownload ? 'nodownload' : null,
          disableFullscreen ? 'nofullscreen' : null,
        ])
          .compact()
          .join(' ')
          .value()}
        style={{
          ...defaultVideoStyle,
          ...videoStyle,
        }}
        onClick={(e) => {
          toggleIsPlaying()
          e.preventDefault()
          e.stopPropagation()
        }}
        onTimeUpdate={(e) => {
          const target = e.target as HTMLVideoElement
          if (!target.paused && range !== undefined) {
            const min = _.get(range, '[0]', 0)
            const max = _.get(range, '[1]', 5)
            if (pauseAtRangeStart) {
              if (target.currentTime >= min) {
                target.pause()
                target.currentTime = min
              }
            }
            if (pauseAtRangeEnd) {
              if (target.currentTime >= max) {
                target.pause()
                target.currentTime = max
              }
            }
            if (target.currentTime > max && onRangeEnd) {
              onRangeEnd()
            } else if (clampToRange === true) {
              if (loop === true) {
                if (target.currentTime < min) target.currentTime = min
                if (target.currentTime > max) target.currentTime = min
              } else {
                if (target.currentTime > max) {
                  target.pause()
                  // target.currentTime = min;
                }
              }
            }
          }
          handleUpdateTime(e)
        }}
        onLoadedMetadata={handleUpdateTime}
        onWaiting={() => {
          if (!videoUrl) return
          setIsBusy(true)
        }}
        onCanPlay={(e) => {
          const t = e.target as HTMLVideoElement
          t.playbackRate = playbackRate || 1
          setIsBusy(false)
        }}
        onError={() => setIsBusy(false)}
        {...rest}
      >
        <track
          kind='metadata'
          src={dataUrl}
          ref={textRef}
          srcLang='en'
          label='English'
          default
        />
      </video>
      <Dimmer active={isBusy}>
        <Loader />
      </Dimmer>
      <div className='video-overlay'>
        {overlay ? overlay : null /* TODO: overlay or dataframe? */}
        {videoUrl && !canPlayCurrentVideo ?
          <div
            style={{
              pointerEvents: 'all',
              background: 'rgba(255, 0, 0, 0.15)',
              position: 'absolute',
              top: 0,
              height: '100%',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div>
              Can't play, video format {videoExtension ? `.${videoExtension}` : null} is not
              supported by this browser!
            </div>
          </div>
        : null}
        {!videoUrl ?
          <div
            style={{
              pointerEvents: 'all',
              background: 'rgba(0, 0, 0, 0.5)',
              position: 'absolute',
              top: 0,
              height: '100%',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            No video loaded
          </div>
        : null}
        <div className='controls'>
          {shouldHandleProgress ?
            <div
              className='progress'
              onClick={handleOnProgressClick}
            >
              <div className='timestamp'>
                <div>{formatTime ? secondsToTimeString(range[0]) : (range[0] || 0).toFixed(2)}</div>
                <div>
                  {formatTime ? secondsToTimeString(currentTime) : (currentTime || 0).toFixed(2)}
                </div>
                <div>{formatTime ? secondsToTimeString(range[1]) : (range[1] || 0).toFixed(2)}</div>
              </div>
              <Progress
                color='blue'
                size='tiny'
                value={(currentTime || 0) - range[0]}
                total={range[1] - range[0]}
              />
            </div>
          : null}
          {actions ?
            <div className='actions'>{actions}</div>
          : null}
        </div>
        {/* {dataFrame === undefined ? null : <div style={{
                    position: 'absolute',
                    border: '1px solid red',
                    width: '50px',
                    height: '50px',
                    top: `${dataFrame.y}px`,
                    left: `${dataFrame.x}px`,
                }}>
                    <h5 style={{
                        color: '#fff',
                    }}>
                        {dataFrame.text}
                    </h5>
                </div>} */}
      </div>
    </div>
  )
})
